import AttendeeTicketNotification from '@eventbrite/attendee-ticket-notification';
import { CoreApplicationContext } from '@eventbrite/context-gen';
import { Layout } from '@eventbrite/eds-layout';
import { isMediumNarrowDown } from '@eventbrite/eds-utils';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { updateUser } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React from 'react';
import { connect } from 'react-redux';
import { ConsumerHeader } from '../../../../components/ConsumerHeader';
import { GA_HOME_FEED_CATEGORY } from '../../../../constants/analytics';
import {
    Buckets,
    Events,
    FeatureFlags,
    PlaceObject,
} from '../../../../types/index';
import { BasePage } from '../../../base';
import withStickBehavior from '../../hoc/withStickyBehavior';
import { updateHeaderImage } from '../../redux/header';
import { getRestOfContext } from '../../redux/root';
import { runSearch } from '../../redux/search';
import { getFeedTrackingProperties } from '../../utils';
import { BrowseContainer } from '../BrowseContainer';

const HeaderContainer = loadable(() => import('../HeaderContainer'), {
    resolveComponent: (components: { HeaderContainer: React.ReactElement }) =>
        components.HeaderContainer,
});
const FullbleedHeaderContainer = loadable(
    () => import('../FullbleedHeaderContainer'),
    {
        resolveComponent: (components: {
            FullbleedHeaderContainer: React.ReactElement;
        }) => components.FullbleedHeaderContainer,
    },
);

const USBannerContainer = loadable(() => import('../USBannerContainer'), {
    resolveComponent: (components: { USBannerContainer: React.ReactElement }) =>
        components.USBannerContainer,
});

const EnhancedHomeConsumerHeader = withStickBehavior(ConsumerHeader);

//Connect BasePage with extra content
const _mapStateToProps = (
    {
        app,
        user,
        content,
    }: {
        app: { isMobile?: boolean; featureFlags: FeatureFlags };
        user: { isAuthenticated: boolean; publicId: string };
        content: { seasonal: any[] };
    },
    ownProps: any,
) => ({
    ...ownProps,
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    featureFlags: app.featureFlags,
    seasonalContent: content.seasonal,
});

const _mapDispatchToProps = {
    handleSearch: runSearch,
};

const ConnectedBasePage = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(BasePage);

const applyIEFixes = () => {
    /**
     * IE is returning errors because SVG elements are not correctly mapped when parsing the docs.
     * This post gives a solution to this: https://github.com/Pomax/react-onclickoutside#ie-does-not-support-classlist-for-svg-elements
     */
    if (!('classList' in SVGElement.prototype)) {
        Object.defineProperty(SVGElement.prototype, 'classList', {
            get() {
                return {
                    contains: (className?: string) =>
                        this.className.baseVal.split(' ').indexOf(className) !==
                        -1,
                };
            },
        });
    }
};

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl: string) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

interface HomeBaseContainerProps {
    buckets: Buckets;
    featureFlags?: FeatureFlags;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    currentPlace?: string;
    currentPlaceParent?: string;
    locations?: {
        places: PlaceObject[];
    };
    placeType?: string;
    slug?: string;
    flatBucket?: {
        results: Events;
    };
    isAuthenticated?: boolean;
    userId?: string;
    handleRestOfContext?: Function;
    originalContext?: object;
    history?: object;
    env?: CoreApplicationContext['env'];
    updateHeaderImage?: Function;
    header?: {
        forceSpecialStyles: boolean;
    };
    app: {
        loadingState: { isFeLoading: boolean };
    };
}

interface HomeBaseContainerState {
    isInBrowser?: boolean;
    isMobile?: boolean;
}

export class HomeBaseContainer extends React.Component<
    HomeBaseContainerProps,
    HomeBaseContainerState
> {
    constructor(props: Readonly<HomeBaseContainerProps>) {
        super(props);

        this.state = {
            isInBrowser: false,
            isMobile: false,
        };

        props.updateHeaderImage?.();
    }

    componentDidMount() {
        const {
            handleRestOfContext,
            originalContext,
            isAuthenticated,
            userId,
        } = this.props;

        applyIEFixes();
        handleRestOfContext?.(originalContext);

        this.setState({
            isInBrowser: true,
            isMobile: isMediumNarrowDown(),
        });

        const feedTrackingProperties = getFeedTrackingProperties(
            isAuthenticated,
            userId,
        );

        trackAnalyticsEvent(feedTrackingProperties);
    }

    componentDidUpdate(prevProps: HomeBaseContainerProps) {
        const { app, userId } = this.props;
        const prevUserId = prevProps?.userId;

        //Only update user when the userId changes
        if (
            this.state.isInBrowser &&
            !app.loadingState.isFeLoading &&
            userId !== prevUserId
        ) {
            updateUser({ user: { userID: userId } });
        }
    }

    render() {
        const { header, env } = this.props;
        //check if call successful
        //hydrate provider with necessary data from store

        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        const overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env?.loginUrl || ''),
                      signinUrl: fixLoginUrl(this.props.env?.signinUrl || ''),
                      signupUrl: fixLoginUrl(this.props.env?.signupUrl || ''),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: false,
                  },
              };

        const useNewHeader = header?.forceSpecialStyles;

        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
            >
                {useNewHeader && (
                    <FullbleedHeaderContainer history={this.props.history} />
                )}
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    <AttendeeTicketNotification
                        shouldAnimateEntry={true}
                        isAuthenticated={this.props.isAuthenticated}
                        containerStyles={
                            this.state.isMobile
                                ? { margin: '0 auto', top: '120px' }
                                : { right: '32px', top: '120px' }
                        }
                        onLinkClick={(
                            eid: string,
                            hasDigitalContent: boolean,
                        ) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: eid,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventViewed'
                                    : 'UpcomingTicketViewed',
                            })
                        }
                        onDismiss={(
                            timeUntilEvent: string,
                            hasDigitalContent: boolean,
                        ) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: timeUntilEvent,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventDismissed'
                                    : 'UpcomingTicketDismissed',
                            })
                        }
                        domNode=""
                        animateEntryDirection="downwards"
                    />
                    {env?.localeInfo.tld === '.com' ? (
                        <div className="eds-l-pad-top-3">
                            <USBannerContainer />
                        </div>
                    ) : null}
                    <>
                        {!useNewHeader && (
                            <HeaderContainer history={this.props.history} />
                        )}
                    </>
                    <BrowseContainer />
                </Layout>
            </ConnectedBasePage>
        );
    }
}

const _mapHomeStateToProps = ({
    app,
    user,
    env,
    header,
}: {
    app: {
        isMobile?: boolean;
        loadingState: { isFeLoading: boolean };
    };
    user: {
        isAuthenticated?: boolean;
        publicId?: string;
    };
    env: CoreApplicationContext['env'];
    header: object;
}) => ({
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    env,
    app,
    header,
});

const _mapHomeDispatchToProps = {
    handleSearch: runSearch,
    handleRestOfContext: getRestOfContext,
    updateHeaderImage: updateHeaderImage,
};

export default connect(
    _mapHomeStateToProps,
    _mapHomeDispatchToProps,
)(HomeBaseContainer);
